import type {
  AtBackOfProp,
  AtDoorByType,
  ElectricalLayout,
  FourLightsWithOptionalFan,
  GarageLights,
  LinearArray,
  RectangularArray,
  SingleLight,
  StandardOutlet,
} from "building-types/electrical/electricalLayoutTypes";
import { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import { LegacyPropPositionType } from "building-types/electrical/electricalLegacyRoomLayoutTypesEnum";
import { ControllerSelectionType, LoadType } from "building-types/electrical/electricalTypesEnum";
import type { GeneratedElectricalGroup } from "building-types/electrical/generatedElectricalTypes";
import { f2i } from "doc/measurementUtils";
import type { ElectricalLayoutOpts } from "../electricalLayoutTypes";
import { atBackOfProp } from "./atBackOfProp";
import { atDoorByType } from "./atDoorByType";
import { fourLightsWithOptionalFan } from "./fourLightsWithOptionalFan";
import { garageLights } from "./garageLights";
import { linearArray } from "./linearArray";
import { rectangularArray } from "./rectArray";
import { roomSingleLightWithOptionalFan } from "./roomSingleLight";
import { standardOutlets } from "./standardOutlets";

export async function makeElectricalLayout(
  electricalLayout: ElectricalLayout,
  opts: ElectricalLayoutOpts,
): Promise<GeneratedElectricalGroup[] | null> {
  const { layoutType } = electricalLayout;
  const electricalLayoutFunc = ElectricalLayoutFuncSelector[layoutType];
  if (!electricalLayoutFunc) {
    console.error(`electrical layout func [${layoutType}] not implemented.`);
    return noLoads();
  }
  const func = electricalLayoutFunc(electricalLayout);
  return await func(opts);
}

const ElectricalLayoutFuncSelector: { [key in ElectricalLayoutType]?: any } = {
  SINGLE_LIGHT: roomSingleLightWithOptionalFan,
  FOUR_LIGHTS: fourLightsWithOptionalFan,
  GARAGE_LIGHTS: garageLights,
  STANDARD_OUTLETS: standardOutlets,
  AT_BACK_OF_PROP: atBackOfProp,
  AT_DOOR_BY_TYPE: atDoorByType,
  LINEAR_ARRAY: linearArray,
  RECTANGULAR_ARRAY: rectangularArray,
};

function noLoads() {
  return null;
}

export const defaultElectricalLayouts = {
  atBackOfProp: {
    layoutType: ElectricalLayoutType.AT_BACK_OF_PROP,
    loadType: LoadType.RECESSED_CEILING_LIGHT,
    propType: "sedan",
    connected: true,
    placements: [
      {
        type: LegacyPropPositionType.OFFSET_FROM_PROP_BACK,
        adjustmentNormal: 0,
        adjustmentTangent: 0,
      },
    ],
    controllerPlacementType: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  } as AtBackOfProp,

  atDoorByType: {
    layoutType: ElectricalLayoutType.AT_DOOR_BY_TYPE,
    doorType: undefined,
    loadType: LoadType.RECESSED_CEILING_LIGHT,
    offset: 10,
    connected: false,
    controllerPlacementType: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  } as AtDoorByType,

  fourLights: {
    layoutType: ElectricalLayoutType.FOUR_LIGHTS,
    loadType: LoadType.RECESSED_CEILING_LIGHT,
    addFan: true,
    cornerInset: 36,
    controllerPlacementType: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  } as FourLightsWithOptionalFan,

  garageLights: {
    layoutType: ElectricalLayoutType.GARAGE_LIGHTS,
    loadType: LoadType.FLOURESCENT_CEILING_LIGHT,
    addOpener: true,
    controllerPlacementType: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  } as GarageLights,

  singleLight: {
    layoutType: ElectricalLayoutType.SINGLE_LIGHT,
    loadType: LoadType.PENDANT_CEILING_LIGHT,
    addBathFan: false,
    addCeilingFan: false,
    singleController: false,
    controllerPlacementType: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  } as SingleLight,

  standardOutlets: {
    layoutType: ElectricalLayoutType.STANDARD_OUTLETS,
    loadType: LoadType.OUTLET,
    singleOutletMax: f2i(12),
    minWallLength: f2i(2.5),
    outletSpacing: f2i(6),
  } as StandardOutlet,

  linearArray: {
    layoutType: ElectricalLayoutType.LINEAR_ARRAY,
    loadType: LoadType.MOUNTED_CEILING_LIGHT,
    quantity: 2,
    spacing: undefined,
    shift: undefined,
    trimToRoom: true,
    controllerPlacementType: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  } as LinearArray,

  rectangularArray: {
    layoutType: ElectricalLayoutType.RECTANGULAR_ARRAY,
    loadType: LoadType.MOUNTED_CEILING_LIGHT,
    xQuantity: 2,
    xSpacing: undefined,
    xShift: undefined,
    trimToRoom: true,
    yQuantity: 2,
    ySpacing: undefined,
    yShift: undefined,
    controllerPlacementType: [{ type: ControllerSelectionType.PRIMARY_ENTRANCE }],
  } as RectangularArray,
};

type DefaultElectricalLayoutTypes = keyof typeof defaultElectricalLayouts;

export const electricalLayoutOptions: { [key in DefaultElectricalLayoutTypes]: string } = {
  atBackOfProp: "At Back of Prop",
  atDoorByType: "At Door By Type",
  singleLight: "Single Load",
  garageLights: "Garage Lights",
  fourLights: "Four Loads",
  standardOutlets: "Edge Loads",
  linearArray: "Linear Array",
  rectangularArray: "Rectangular Array",
};
